import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.space';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'The Ultimate VigorBuy Spreadsheet Templates Collection for Business Success',
  description: 'A curated library of ready-to-use spreadsheet templates covering ecommerce, finance, marketing, operations, and project management — designed to eliminate setup time and accelerate your workflow.',
  author: {
    '@type': 'Organization',
    name: 'VigorBuy Spread Team',
  },
  publisher: {
    '@type': 'Organization',
    name: 'VigorBuy Spread',
    url: siteUrl,
  },
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/vigorbuy-spreadsheet-templates`,
  },
  inLanguage: 'en',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Templates Library',
      item: `${siteUrl}/vigorbuy-spreadsheet-templates`,
    },
  ],
};

const templateCategories = [
  {
    icon: 'ri-shopping-cart-2-line',
    title: 'Ecommerce Order Tracker',
    desc: 'Consolidate multi-channel orders into a single dashboard with automated SKU-level profitability calculations and real-time inventory syncing across platforms.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Financial Cash Flow Planner',
    desc: 'Track inflows and outflows with categorized expense tracking, automated balance forecasts, and month-over-month variance analysis for smarter budgeting.',
  },
  {
    icon: 'ri-store-line',
    title: 'Inventory Management System',
    desc: 'Monitor stock levels, set automatic reorder alerts when inventory drops below thresholds, and generate supplier-ready purchase orders with one click.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Sales Performance Dashboard',
    desc: 'Visualize revenue trends, top-selling products, and sales rep performance with dynamic charts that update as new data flows in throughout the quarter.',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Marketing Campaign Tracker',
    desc: 'Compare ROI across channels — email, social, paid ads — with cost-per-acquisition calculations and automated performance scoring for each campaign.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Project Management Workflow',
    desc: 'Assign tasks, track milestones, monitor resource allocation, and generate stakeholder-ready progress reports from a single coordinated spreadsheet.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Customer Relationship Manager',
    desc: 'Organize leads, track follow-up communications, segment customers by purchase history, and schedule automated check-in reminders.',
  },
  {
    icon: 'ri-file-chart-line',
    title: 'Expense Report Generator',
    desc: 'Capture receipts, categorize claims by department or project, and auto-generate formatted expense reports ready for accounting review and approval.',
  },
  {
    icon: 'ri-global-line',
    title: 'Multi-Currency Converter',
    desc: 'Handle international transactions with live exchange rate integration, automatic conversion calculations, and consolidated reporting in your base currency.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Recurring Billing Scheduler',
    desc: 'Track subscription customers, automate renewal reminders, calculate churn rates, and forecast recurring revenue with built-in scenario modeling.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Shipping & Logistics Tracker',
    desc: 'Monitor shipments with carrier integration, calculate delivery costs by region, and generate packing slips and shipping labels from order data.',
  },
  {
    icon: 'ri-pie-chart-line',
    title: 'KPI Scorecard Dashboard',
    desc: 'Define your key performance indicators, set targets and thresholds, and get a visual red-amber-green health check of your entire business at a glance.',
  },
];

export default function TemplatesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <article className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <nav className="mb-8">
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors flex items-center gap-1.5">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">Templates</span>
              <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold">Updated 2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-900 leading-tight mb-4">
              The Ultimate VigorBuy Spreadsheet Templates Collection for Business Success
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              A curated library of ready-to-use spreadsheet templates covering ecommerce, finance, marketing, operations, and project management — designed to eliminate setup time and accelerate your workflow.
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20organized%20desk%20workspace%20with%20multiple%20neatly%20arranged%20template%20documents%20and%20colorful%20tab%20dividers%2C%20soft%20warm%20cream%20background%20with%20gentle%20coral%20accent%20touches%2C%20modern%20minimalist%20aesthetic%2C%20natural%20diffused%20light%20from%20window%2C%20editorial%20photography%20style%20with%20soft%20shadows%2C%20harmonious%20composition%20with%20structured%20layout%2C%20no%20text%2C%20no%20faces%2C%20no%20screens%20with%20visible%20content&width=1200&height=600&seq=templates-hero-2026-v2&orientation=landscape"
              alt="Spreadsheet templates collection workspace"
              className="w-full h-auto object-cover object-center"
            />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Why Templates Transform Your Workflow</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              The average business professional spends over 40% of their spreadsheet time on formatting, formula setup, and data structure design — tasks that add zero analytical value. <strong>VigorBuy Spreadsheet templates</strong> eliminate this wasted effort by providing production-ready frameworks that you can start using within minutes of downloading.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Each template in this collection has been battle-tested by hundreds of businesses across industries. They include pre-configured formulas with built-in error checking, conditional formatting rules that highlight anomalies automatically, and data validation settings that prevent common input mistakes before they corrupt your analysis.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Rather than spending hours searching for the right template, customizing generic ones, or building from scratch, you can browse this organized library and find exactly what your business needs — whether you are launching a new ecommerce store, scaling a growing operation, or optimizing an established enterprise workflow. Check our <Link to="/vigorbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium underline">complete guide</Link> for step-by-step template setup instructions.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">Featured Template Categories</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templateCategories.map((tpl) => (
                <div key={tpl.title} className="group p-5 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:shadow-md transition-all duration-300">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center mb-3 group-hover:bg-primary-100 transition-colors duration-300">
                    <i className={`${tpl.icon} text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">{tpl.title}</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">{tpl.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How To Get The Most From These Templates</h2>
            <div className="bg-background-100 rounded-2xl p-6 space-y-4">
              {[
                { step: '01', title: 'Pick the Right Template', desc: 'Match your immediate business pain point. If inventory discrepancies consume your Mondays, start with the Inventory Management System. Solve one problem well before expanding.' },
                { step: '02', title: 'Customize Thoughtfully', desc: 'Every template includes marked customization zones. Change these areas freely — but preserve the core formulas and structure that handle calculations and error checking automatically.' },
                { step: '03', title: 'Set a Review Cadence', desc: 'Schedule 15 minutes weekly to review your templates. Update assumptions, refresh data connections, and verify that conditional formatting rules are still triggering correctly.' },
                { step: '04', title: 'Share With Your Team', desc: 'Most templates include role-based access settings. Share view-only links with stakeholders, edit access with collaborators, and protected ranges for sensitive formula cells.' },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{item.step}</span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Industries That Benefit Most</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              While <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline">VigorBuy Spreadsheet</Link> templates serve virtually any data-driven business, certain industries experience particularly dramatic improvements. Ecommerce sellers report 45% less time on daily operations after adopting the order and inventory templates. Marketing agencies consolidate client reporting from 8 hours to under 90 minutes using the campaign tracker. Small retail shops reduce stockout incidents by 60% through automated reorder alerts.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              The common thread across all high-performing adopters is consistency. Businesses that integrate templates into their daily routines — rather than treating them as occasional tools — see compounding efficiency gains over weeks and months. Explore our <Link to="/vigorbuy-spreadsheet-comparison" className="text-primary-500 hover:text-primary-600 font-medium underline">platform comparison</Link> to understand how these templates work across Excel, Google Sheets, and other spreadsheet environments.
            </p>
          </section>

          <section className="mb-10">
            <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-primary-50 border border-primary-200">
              <div className="flex-1">
                <h3 className="font-heading font-bold text-foreground-900 text-lg mb-1">Access the Full Template Library</h3>
                <p className="text-foreground-600 text-sm">Explore all business templates and start streamlining your workflow today.</p>
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-all duration-200 shadow-lg shadow-primary-500/20 whitespace-nowrap text-center cursor-pointer"
              >
                Visit Main Store
              </a>
            </div>
          </section>

          <div className="border-t border-background-200/70 pt-8 mt-8">
            <p className="text-foreground-500 text-xs text-center mb-4">
              This template guide is part of the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">VigorBuy Spread</Link> knowledge base. Also check our <Link to="/vigorbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium">complete guide</Link>, <Link to="/vigorbuy-spreadsheet-comparison" className="text-primary-500 hover:text-primary-600 font-medium">platform comparison</Link>, and <Link to="/vigorbuy-spreadsheet-security" className="text-primary-500 hover:text-primary-600 font-medium">security guide</Link>.
            </p>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}